import type { HTMLAttributes, ReactNode } from 'react';

import React from 'react';
import { useTranslation } from 'react-i18next';

import { EyeIcon, InfoCircleIcon } from '../../icons';
import { classNames } from '../../utils/class-names';
import { Tooltip } from '../tooltip';

import styles from './input-description.module.css';

export type InputDescriptionProps = {
  inputId?: string;
  label?: string | ReactNode;
  hint?: string;
  hintDelay?: number;
  errorMessage?: string;
  helpMessage?: string;
  passwordVisible?: boolean;
  onPasswordToggle?: () => void;
  length?: number;
  maxLength?: number;
  disabled?: boolean;
  children: ReactNode;
} & HTMLAttributes<HTMLElement>;

/**
 * Wrapper component for an input to show optional UI elements.
 *
 * @param {string} [inputId] Id of the input which is wrapped. Used to give a proper id to the label and error message.
 * @default undefined
 * @param {string | ReactNode} [label] Label for the wrapped input.
 * @default undefined
 * @param {string} [hint] Hint for the wrapped input.
 * @default undefined
 * @param {number} [hintDelay] The delay in milliseconds before the hint tooltip is shown
 * @default undefined
 * @param {string} [errorMessage] Error message for the wrapped input.
 * @default undefined
 * @param {string} [helpMessage] Help message for the wrapped input.
 * @default undefined
 * @param {boolean} [passwordVisible] Whether the password is visible or not.
 * @default undefined
 * @param {() => void} [onPasswordToggle] Callback to toggle the password visibility.
 * @default undefined
 * @param {number} [length] Length of the wrapped input's value.
 * @default undefined
 * @param {number} [maxLength] Maximum length of the wrapped input's value.
 * @default undefined
 * @param {boolean} [disabled] Whether the input is disabled or not.
 * @default false
 * @param {ReactNode} [children] The content which is wrapped.
 * @default undefined
 */
export function InputDescription({
  inputId,
  label,
  hint,
  hintDelay,
  errorMessage,
  helpMessage,
  passwordVisible,
  onPasswordToggle,
  length,
  maxLength,
  disabled,
  children,
  ...props
}: InputDescriptionProps) {
  const { t } = useTranslation();

  return (
    <div {...props} className={classNames(styles.labelWrapper, disabled && styles.disabled, props.className)}>
      <header className={styles.header}>
        {label && (
          <div className={styles.label}>
            <label data-testid={inputId && `${inputId}-label`} htmlFor={inputId}>
              {label}
            </label>

            {hint && (
              <Tooltip data-testid={inputId && `${inputId}-tooltip`} label={hint} delay={hintDelay}>
                <InfoCircleIcon className={styles.hint} />
              </Tooltip>
            )}
          </div>
        )}

        {onPasswordToggle && (
          <button
            type="button"
            data-testid={inputId && `${inputId}-password-toggle`}
            className={styles.passwordToggle}
            aria-label={passwordVisible ? t('inputDescription.hidePassword') : t('inputDescription.showPassword')}
            onClick={onPasswordToggle}
          >
            <EyeIcon />
            {passwordVisible ? t('inputDescription.hidePassword') : t('inputDescription.showPassword')}
          </button>
        )}
      </header>

      {children}

      {(helpMessage || errorMessage || (length !== undefined && maxLength !== undefined)) && (
        <footer className={styles.footer}>
          <div className={styles.footerLeft}>
            {helpMessage && (
              <div data-testid={inputId && `${inputId}-help`} className={styles.helpMessage}>
                {helpMessage}
              </div>
            )}

            <div
              data-testid={inputId && `${inputId}-error`}
              aria-hidden={!errorMessage}
              className={styles.validationError}
            >
              <span>
                <InfoCircleIcon />
                {errorMessage}
              </span>
            </div>
          </div>

          {length !== undefined && maxLength !== undefined && (
            <div data-testid={inputId && `${inputId}-counter`} className={styles.counter}>
              {length}/{maxLength}
            </div>
          )}
        </footer>
      )}
    </div>
  );
}
